import axios, { type AxiosInstance, isAxiosError } from "axios";
import i18n from "../locales/i18n";
import { config } from "../config";
import { vgsPostUrl } from "../utils/tokenization-config";
import type {
  IWidgetSession,
  ITermsBlock,
} from "../shared/session/interfaces/session";
import { LINK_ERROR_CODE, RequestStatus } from "../utils/commons";
import { resolveUrlLinkId } from "../utils/resolveUrlLinkId";
import { resolveXLinkIdHeader } from "./resolveXLinkIdHeader";
import { useSessionStore } from "../store/useSessionStore";
import { useOfflineStore } from "../store/useOfflineStore";
import { isAxiosNetworkFailure } from "../utils/axiosNetworkError";
import { getDeviceSession } from "../device/seon-session";
import type {
  ICardPaymentMethod,
  IAllPaymentMethods,
  IAddCardPaymentMethodRequest,
  IPreValidateCardRequest,
  IPreValidateCardResult,
  ICardUsageReadinessInput,
  ICardUsageReadinessResult,
  ICardProvisionInput,
  ICardProvisionResult,
} from "../interfaces/payment-method";

/**
 * Error class for link claim errors
 */
export class LinkClaimError extends Error {
  constructor(
    message: string,
    public code: LINK_ERROR_CODE,
    public statusCode?: number,
    public details?: unknown,
  ) {
    super(message);
    this.name = "LinkClaimError";
  }
}

export interface WidgetApiError {
  code: string;
  message: string;
  details?: unknown;
}

/** Extracts the normalized `{ code, message }` payload from a widget API error. */
export const getWidgetApiError = (error: unknown): WidgetApiError | null => {
  if (!isAxiosError(error)) return null;
  const body = error.response?.data?.error;
  if (!body || typeof body.code !== "string") return null;
  return {
    code: body.code,
    message: typeof body.message === "string" ? body.message : "",
    details: body.details,
  };
};

/**
 * Codes that make the link permanently unusable: the widget cannot recover from
 * them and must show the matching explanation instead of a generic error.
 */
const DEAD_LINK_CODES: string[] = [
  LINK_ERROR_CODE.LINK_NOT_FOUND,
  LINK_ERROR_CODE.LINK_EXPIRED,
  LINK_ERROR_CODE.LINK_REVOKED,
];

/**
 * Resolves the claim/API failure into a link error code. The backend `code`
 * wins over the HTTP status: a revoked link answers 409 `LINK_REVOKED` on
 * claim and 403 `LINK_REVOKED` on the partner CIP ticket, so status alone
 * cannot tell "revoked" from "already used".
 */
const resolveLinkErrorCode = (
  status: number | undefined,
  apiCode: string | undefined
): LINK_ERROR_CODE => {
  if (apiCode && apiCode in LINK_ERROR_CODE) {
    return apiCode as LINK_ERROR_CODE;
  }

  switch (status) {
    case 400:
      return LINK_ERROR_CODE.INVALID_TOKEN;
    case 404:
      return LINK_ERROR_CODE.LINK_NOT_FOUND;
    case 409:
      return LINK_ERROR_CODE.LINK_ALREADY_USED;
    case 410:
      return LINK_ERROR_CODE.LINK_EXPIRED;
    case 429:
      return LINK_ERROR_CODE.RATE_LIMITED;
    default:
      // No status means the request never reached the backend (network/CORS).
      return status === undefined || status >= 500
        ? LINK_ERROR_CODE.SERVICE_UNAVAILABLE
        : LINK_ERROR_CODE.INTERNAL_ERROR;
  }
};

/**
 * Custom event for dead links (LINK_NOT_FOUND, LINK_EXPIRED, LINK_REVOKED).
 * Dispatched when the API returns these errors on any widget endpoint.
 */
export const SESSION_EXPIRED_EVENT = "raas-widgets-flow-lola-session-expired";

export const MAINTENANCE_EVENT = "raas-widgets-flow-lola-maintenance";

export interface MaintenanceStatus {
  active: boolean;
  title?: string;
  body?: string;
  supportEmail?: string;
  supportWhatsappNumber?: string;
}
export const LINK_ALREADY_USED_EVENT = "raas-widgets-flow-lola-link-already-used";

/**
 * True when the failure means the link itself died (expired / revoked / gone).
 * `useSessionExpiredHandler` already routes those to the explanatory screen, so
 * callers must not overwrite it with their own generic error copy.
 */
export const isDeadLinkError = (error: unknown): boolean => {
  if (!isAxiosError(error)) return false;
  const code = error.response?.data?.error?.code;
  return DEAD_LINK_CODES.includes(code) || error.response?.status === 410;
};

/**
 * Current link ID for API requests. Set after a successful claim.
 */
let currentLinkId: string | null = null;

/**
 * Set the current link ID for API requests (adds the x-link-id header).
 */
export const setWidgetLinkId = (linkId: string | null) => {
  currentLinkId = linkId;
};

/** In-memory link id last set after claim / interceptor (this tab). */
export const getWidgetLinkId = (): string | null => currentLinkId;

/**
 * Silent liveness check. Uses `widgetApi` so a dead link fires
 * `SESSION_EXPIRED_EVENT`. Callers treat network/5xx as fail-open.
 */
export const probeLinkSession = async (
  linkId: string,
): Promise<{
  expiresAt: string | null;
  config?: {
    usage?: { state?: string; operationId?: string; consumedAt?: string };
  };
}> => {
  const response = await widgetApi.get(`/widget/links/${linkId}/session`, {
    raasSilentOffline: true,
  });
  const body = response.data?.data ?? response.data ?? {};
  const expiresAt = body?.expiresAt;
  return {
    ...body,
    expiresAt: typeof expiresAt === "string" ? expiresAt : null,
  };
};

export const fetchMaintenanceStatus = async (
  linkId: string,
  locale: string,
): Promise<MaintenanceStatus> => {
  const { data } = await widgetApi.get(
    `/widget/links/${linkId}/maintenance`,
    { params: { locale }, raasSilentOffline: true },
  );
  const payload = data?.data;
  if (!payload?.active) return { active: false };
  return {
    active: true,
    title: typeof payload.title === "string" ? payload.title : undefined,
    body: typeof payload.body === "string" ? payload.body : undefined,
    supportEmail:
      typeof payload.supportEmail === "string" ? payload.supportEmail : undefined,
    supportWhatsappNumber:
      typeof payload.supportWhatsappNumber === "string"
        ? payload.supportWhatsappNumber
        : undefined,
  };
};

export const dispatchSessionExpiredEvent = (detail: {
  code: string;
  details?: unknown;
}): void => {
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT, { detail }));
};

function extractRequestLinkId(req: { headers?: unknown; url?: string } | undefined): string | null {
  if (!req) return null;
  const headers = req.headers as Record<string, unknown> | undefined;
  const header = headers?.["x-link-id"] ?? headers?.["X-Link-Id"];
  if (typeof header === "string" && header.length > 0) return header;
  const match = (req.url ?? "").match(/\/widget\/links\/([^/?]+)/);
  return match?.[1] ?? null;
}

export function shouldBroadcastSessionExpired(req: {
  headers?: unknown;
  url?: string;
} | undefined): boolean {
  if (useSessionStore.getState().session.status === RequestStatus.LOADING) {
    return false;
  }
  const reqLinkId = extractRequestLinkId(req);
  const urlLinkId = resolveUrlLinkId();
  if (reqLinkId && urlLinkId && reqLinkId !== urlLinkId) {
    return false;
  }
  return true;
}

/**
 * Axios instance for authenticated widget API calls (x-link-id header).
 */
const ngrokBrowserHeaders = (): Record<string, string> =>
  typeof window !== "undefined" && window.location.hostname.includes("ngrok")
    ? { "ngrok-skip-browser-warning": "true" }
    : {};

const createWidgetApiInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: config.API_BASE_URL,
  });

  instance.interceptors.request.use((requestConfig) => {
    Object.assign(requestConfig.headers, ngrokBrowserHeaders());
    // Bypass a disk-cached 410 on header-only GETs (catalog URL has no linkId).
    const method = (requestConfig.method ?? "get").toLowerCase();
    if (method === "get") {
      requestConfig.headers["Cache-Control"] = "no-cache";
      requestConfig.headers["Pragma"] = "no-cache";
    }
    const urlLinkId = resolveUrlLinkId();
    const persisted =
      useSessionStore.getState().session.data?.linkId ?? null;
    if (urlLinkId && currentLinkId !== urlLinkId) {
      setWidgetLinkId(urlLinkId);
    }
    const header = resolveXLinkIdHeader({
      urlLinkId,
      currentLinkId,
      persistedLinkId: persisted,
    });
    if (header) {
      requestConfig.headers["x-link-id"] = header;
    }
    return requestConfig;
  });

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      const errorCode = error?.response?.data?.error?.code;
      const statusCode = error?.response?.status;

      if (errorCode === LINK_ERROR_CODE.SERVICE_UNAVAILABLE_MAINTENANCE) {
        window.dispatchEvent(
          new CustomEvent(MAINTENANCE_EVENT, {
            detail: error?.response?.data?.error?.details,
          }),
        );
      }

      if (
        (DEAD_LINK_CODES.includes(errorCode) || statusCode === 410) &&
        shouldBroadcastSessionExpired(error?.config)
      ) {
        window.dispatchEvent(
          new CustomEvent(SESSION_EXPIRED_EVENT, {
            detail: {
              code: errorCode || LINK_ERROR_CODE.LINK_EXPIRED,
              details: error?.response?.data?.error?.details,
            },
          })
        );
      }

      if (
        errorCode === LINK_ERROR_CODE.LINK_ALREADY_USED &&
        shouldBroadcastSessionExpired(error?.config)
      ) {
        window.dispatchEvent(
          new CustomEvent(LINK_ALREADY_USED_EVENT, {
            detail: {
              code: LINK_ERROR_CODE.LINK_ALREADY_USED,
              details: error?.response?.data?.error?.details,
            },
          }),
        );
      }

      if (
        isAxiosNetworkFailure(error) &&
        error.config?.raasSilentOffline !== true
      ) {
        useOfflineStore.getState().reportOffline();
      }

      return Promise.reject(error);
    }
  );

  return instance;
};

export const widgetApi = createWidgetApiInstance();

/**
 * Device info to send with the claim request.
 */
export interface IDeviceInfo {
  userAgent?: string;
  language?: string;
  timezone?: string;
}

/**
 * Claim a magic link by linkId.
 * POST /widget/links/:linkId/claim
 *
 * The response contains the partner configuration — including the theme that
 * the widget must apply and the link-scoped presentation flags
 * (`config.showSplash`; `config.showAgreement` is deprecated and ignored).
 */
export const claimLink = async (
  linkId: string,
  deviceInfo?: IDeviceInfo,
  lang?: string,
  seonSession?: string
): Promise<IWidgetSession> => {
  const apiBaseUrl = config.API_BASE_URL;

  try {
    const { data } = await axios.post(
      `${apiBaseUrl}/widget/links/${linkId}/claim`,
      {
        deviceInfo,
        ...(seonSession ? { seonSession } : {}),
      },
      {
        params: lang ? { lang } : undefined,
        headers: ngrokBrowserHeaders(),
      }
    );

    return {
      sessionId: data.data.sessionId,
      linkId: data.data.linkId,
      token: data.data.token,
      status: data.data.status,
      expiresAt: data.data.expiresAt,
      partner: {
        partnerId: data.data.partner.partnerId,
        name: data.data.partner.name,
        primaryColor: data.data.partner.primaryColor,
        theme: data.data.partner.theme,
        supportEmail: data.data.partner.supportEmail,
        supportWhatsappNumber: data.data.partner.supportWhatsappNumber,
        sessionExpiredRedirectUrl: data.data.partner.sessionExpiredRedirectUrl,
      },
      userData: data.data.userData,
      config: data.data.config,
      language: data.data.language,
      flavor: data.data.flavor,
      themeMode: data.data.themeMode,
      termsAccepted: data.data.termsAccepted,
      termsAcceptedAt: data.data.termsAcceptedAt,
      terms: data.data.terms,
    };
  } catch (error) {
    const status = isAxiosError(error) ? error.response?.status : undefined;
    const apiError = getWidgetApiError(error);
    const code = resolveLinkErrorCode(status, apiError?.code);

    // The message is only a diagnostic fallback: the error screen resolves the
    // user-facing copy from `code` via i18n.
    throw new LinkClaimError(
      apiError?.message || `Failed to claim link (${code})`,
      code,
      status,
      apiError?.details,
    );
  }
};

/**
 * Record the Terms & Conditions / E-Sign acceptance for the current user.
 * Persisted at the user level by the backend so the same user is not prompted
 * again on future links.
 * POST /widget/links/:linkId/accept-terms
 */
export interface IAcceptTermsResponse {
  termsAccepted: boolean;
  termsAcceptedAt?: string;
  termsAcceptedVersion?: string;
}

export const acceptTerms = async (
  linkId: string,
  options?: {
    version?: string;
    documents?: string[];
    acceptedEntryIds?: string[];
    language?: string;
  }
): Promise<IAcceptTermsResponse> => {
  const deviceInfo: IDeviceInfo = {
    userAgent: navigator.userAgent,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };

  const { data } = await widgetApi.post(
    `/widget/links/${linkId}/accept-terms`,
    {
      version: options?.version,
      documents: options?.documents,
      acceptedEntryIds: options?.acceptedEntryIds,
      language: options?.language ?? i18n.language,
      deviceInfo,
    }
  );

  return data.data;
};

export interface ICancelLinkResponse {
  linkId: string;
  referenceId: string;
  flow: string;
  cancelledAt: string;
  reason: string;
}

/**
 * Signal that the user left the flow before creating an operation.
 * POST /widget/links/:linkId/cancel
 *
 * Emits partner webhook `link.cancelled`. Does **not** revoke the link.
 */
export const cancelLink = async (
  linkId: string,
  body?: { reason?: "user_cancel" },
): Promise<ICancelLinkResponse> => {
  const { data } = await widgetApi.post(`/widget/links/${linkId}/cancel`, {
    reason: body?.reason ?? "user_cancel",
  });
  return data.data;
};

export interface ILegalDocumentResponse {
  html: string;
}

/**
 * Fetch a legal document (E-Sign, Terms, etc.) as ready-to-render HTML.
 * GET /widget/legal/:doc/:lang
 */
export const getLegalDocument = async (
  doc: string,
  lang: string
): Promise<ILegalDocumentResponse> => {
  const { data } = await widgetApi.get(`/widget/legal/${doc}/${lang}`);
  return data.data;
};

/**
 * Fetch the partner Terms & Conditions for the link in a specific language.
 * The backend serves manually authored per-language content and falls back to
 * English when the requested language is unavailable.
 * GET /widget/links/:linkId/terms?lang=xx
 */
export const getTerms = async (
  linkId: string,
  lang?: string
): Promise<ITermsBlock | null> => {
  const { data } = await widgetApi.get(`/widget/links/${linkId}/terms`, {
    params: lang ? { lang } : undefined,
  });
  return data.data?.terms ?? null;
};

export type QuoteAnchor = "send" | "receive";
export type PreQuoteVary = "payer" | "fundingMethod";
export type PreQuoteInclude = "options" | "summary" | "facets";

export interface IPreQuoteRequest {
  originCountryCode: string;
  destinationCountryCode: string;
  amount: number;
  anchor?: QuoteAnchor;
  destinationCurrency: string;
  sourceCurrency?: string;
  fundingMethod?: string;
  payoutMethod?: string;
  payerCode?: string;
  /** Destination state (`MEX` or `MX-MEX`). Pins the representative SKU. */
  stateCode?: string;
  vary?: PreQuoteVary;
  include?: PreQuoteInclude[];
}

export interface IEstimateOption {
  capabilityKey: string;
  corridorCode: string;
  payerCode: string | null;
  payoutMethod: string;
  fundingMethod: string;
  sendAmount: number;
  receiveAmount: number;
  customerFee: number;
  customerFxRate: number;
  sourceCurrency: string;
  destinationCurrency: string;
}

export interface IEstimateRange {
  min: number;
  max: number;
  avg: number;
}

export interface IEstimateSummary {
  count: number;
  best?: IEstimateOption;
  worst?: IEstimateOption;
  rankingMetric: "receiveAmount" | "sendAmount";
  receiveAmount?: IEstimateRange;
  sendAmount?: IEstimateRange;
  asOf: string;
  indicative: true;
}

export interface IEstimateFacets {
  fundingMethods?: Array<{
    fundingMethod: string;
    best: { corridorCode: string };
  }>;
  payoutMethods?: Array<{
    payoutMethod: string;
    best: { corridorCode: string };
  }>;
  payers?: Array<{ payerCode: string; best: { corridorCode: string } }>;
}

export interface IEstimateResult {
  options: IEstimateOption[];
  summary: IEstimateSummary;
  facets?: IEstimateFacets;
}

export const getPreQuote = async (
  input: IPreQuoteRequest,
): Promise<IEstimateResult> => {
  const { data } = await widgetApi.post(`/widget/pre-quote`, input, {
    timeout: 40_000,
  });
  return data.data;
};

export interface IResolveLimitsRequest {
  corridorCode: string;
  originCountryCode?: string;
  destinationCountryCode?: string;
  payoutMethod?: string;
  payerCode?: string;
  fundingMethod?: string;
  sendCurrency?: string;
}

export interface IAmountBound {
  minSend: number | null;
  maxSend: number | null;
  currency: string | null;
  bounded: boolean;
}

export const resolveAmountLimits = async (
  input: IResolveLimitsRequest,
): Promise<IAmountBound> => {
  const { data } = await widgetApi.post(`/widget/limits/resolve`, input);
  return data.data.amount;
};

export interface IQuoteRequest {
  sourceCountry: string;
  destinationCountry: string;
  fundingMethod: string;
  payoutMethod: string;
  capabilityKey: string;
  anchor?: QuoteAnchor;
  amount: number;
  currency: string;
  destinationCurrency?: string;
  cardId?: string;
}

export interface IQuoteResponse {
  quoteId: string;
  sendAmount: number;
  receiveAmount: number;
  exchangeRate: number;
  fee?: number;
  totalFee?: number;
  totalCost: number;
  sourceCurrency: string;
  destinationCurrency: string;
  fundingMethod: string;
  payoutMethod: string;
  expiresAt: string;
  signedQuote: string;
  cardId?: string;
  capabilityKey?: string;
  anchor?: QuoteAnchor;
}

export const getQuote = async (
  request: IQuoteRequest,
  options?: { raasSilentOffline?: boolean },
): Promise<IQuoteResponse> => {
  const { data } = await widgetApi.post(`/widget/operations/quote`, request, {
    raasSilentOffline: options?.raasSilentOffline,
  });
  return {
    ...data.data,
    capabilityKey: request.capabilityKey,
    anchor: request.anchor,
  };
};

type VgsAliasField = "card_number" | "card_cvc";

type VgsEchoBody = {
  json?: Partial<Record<VgsAliasField, string>>;
} & Partial<Record<VgsAliasField, string>>;

const pickAlias = (
  data: VgsEchoBody | null | undefined,
  field: VgsAliasField,
): string | undefined => {
  const fromJson = data?.json?.[field];
  if (typeof fromJson === "string" && fromJson.length > 0) return fromJson;
  const top = data?.[field];
  if (typeof top === "string" && top.length > 0) return top;
  return undefined;
};

const isVgsAlias = (value: string | undefined): value is string =>
  typeof value === "string" && value.startsWith("tok_");

export const tokenizeCard = async (
  pan: string,
  cvv: string,
): Promise<{ numberToken: string; cvvToken: string }> => {
  const vgsProxyUrl = await vgsPostUrl();
  const { data } = await axios.post<VgsEchoBody>(
    vgsProxyUrl,
    { card_number: pan, card_cvc: cvv },
    { headers: { "Content-Type": "application/json" } },
  );
  const numberToken = pickAlias(data, "card_number");
  const cvvToken = pickAlias(data, "card_cvc");
  if (!isVgsAlias(numberToken) || !isVgsAlias(cvvToken)) {
    throw new Error("VGS_TOKENIZE_FAILED");
  }
  return { numberToken, cvvToken };
};

export const preValidateCardPaymentMethod = async (
  linkId: string,
  card: IPreValidateCardRequest,
): Promise<IPreValidateCardResult> => {
  const { data } = await widgetApi.post<
    IPreValidateCardResult | { success: boolean; data: IPreValidateCardResult }
  >(`/widget/links/${linkId}/payment-methods/cards/pre-validate`, card, {
    timeout: 40_000,
  });
  if (data && typeof data === "object" && "validationId" in data) {
    return data;
  }
  return (data as { data: IPreValidateCardResult }).data;
};

export const tokenizeCvv = async (cvv: string): Promise<string> => {
  const vgsProxyUrl = await vgsPostUrl();
  const { data } = await axios.post<VgsEchoBody>(
    vgsProxyUrl,
    { card_cvc: cvv },
    { headers: { "Content-Type": "application/json" } },
  );
  const token = pickAlias(data, "card_cvc");
  if (!isVgsAlias(token)) throw new Error("CVV_TOKENIZE_FAILED");
  return token;
};

export const cardUsageReadiness = async (
  linkId: string,
  cardId: string,
  input: ICardUsageReadinessInput,
): Promise<ICardUsageReadinessResult> => {
  const { data } = await widgetApi.post<{
    success: boolean;
    data: ICardUsageReadinessResult;
  }>(
    `/widget/links/${linkId}/payment-methods/cards/${cardId}/usage-readiness`,
    input,
  );
  return data.data;
};

export const provisionCard = async (
  linkId: string,
  cardId: string,
  input: ICardProvisionInput,
): Promise<ICardProvisionResult> => {
  const { data } = await widgetApi.post<{
    success: boolean;
    data: ICardProvisionResult;
  }>(`/widget/links/${linkId}/payment-methods/cards/${cardId}/provision`, input, {
    timeout: 60_000,
  });
  return data.data;
};

export const tokenizeDestinationCard = async (
  pan: string,
): Promise<{ numberToken: string }> => {
  const vgsProxyUrl = await vgsPostUrl();
  const { data } = await axios.post<VgsEchoBody>(
    vgsProxyUrl,
    { card_number: pan },
    { headers: { "Content-Type": "application/json" } },
  );
  const numberToken = pickAlias(data, "card_number");
  if (!isVgsAlias(numberToken)) throw new Error("VGS_TOKENIZE_FAILED");
  return { numberToken };
};

export const addCardPaymentMethod = async (
  linkId: string,
  card: IAddCardPaymentMethodRequest,
): Promise<ICardPaymentMethod> => {
  const { data } = await widgetApi.post(
    `/widget/links/${linkId}/payment-methods/cards`,
    card,
  );
  return data.data;
};

export const removeCardPaymentMethod = async (
  linkId: string,
  cardId: string,
): Promise<void> => {
  await widgetApi.delete(
    `/widget/links/${linkId}/payment-methods/cards/${cardId}`,
  );
};

export const getAllPaymentMethods = async (
  linkId: string,
): Promise<IAllPaymentMethods> => {
  const { data } = await widgetApi.get(
    `/widget/links/${linkId}/payment-methods`,
  );
  const payload = data.data as IAllPaymentMethods;
  return {
    ...payload,
    cards: (payload.cards ?? []).map((c) => ({
      ...c,
      brand: (c.brand || "").toUpperCase(),
    })),
  };
};

export interface ICipReadinessResponse {
  shouldRunCip: boolean;
  reason: string;
  cipTicketUrl: string | null;
  cipTicketId: string | null;
  missingFields?: string[];
}

export const checkCipReadiness = async (
  linkId: string,
): Promise<ICipReadinessResponse> => {
  const { data } = await widgetApi.get(`/widget/links/${linkId}/cip-readiness`);
  return data.data;
};

export interface ICreateOperationRequest {
  signedQuote: string;
  fundingPaymentMethodId?: string;
  /** Confirm-time VGS CVV alias when usage-readiness asked for CVV. Never persisted client-side. */
  fundingCvvToken?: string;
  payoutPaymentMethodId?: string;
  notes?: string;
  seonSession?: string;
  recipient?: {
    name: {
      firstName: string;
      lastName: string;
      middleName?: string;
      secondLastName?: string;
    };
    address?: {
      countryISO2?: string;
      state?: string;
      city?: string;
      line1?: string;
      zip?: string;
    };
    phone?: string;
  };
}

export interface IOperationResponse {
  operationId: string;
  quoteId: string;
  status: string;
  sendAmount: number;
  receiveAmount: number;
  exchangeRate: number;
  fee?: number;
  fundingFee?: number;
  transactionFee?: number;
  taxFee?: number;
  totalFee?: number;
  totalCost: number;
  sourceCurrency: string;
  destinationCurrency: string;
  fundingPaymentMethodId?: string;
  payoutPaymentMethodId?: string;
  notes?: string;
  fundingMethod?: string;
  fundingStatus?: string;
  recipient?: ICreateOperationRequest["recipient"];
  createdAt: string;
}

export const createOperation = async (
  linkId: string,
  request: ICreateOperationRequest,
): Promise<IOperationResponse> => {
  const seonSession =
    request.seonSession ?? (await getDeviceSession()) ?? undefined;
  const { data } = await widgetApi.post(`/widget/links/${linkId}/operations`, {
    ...request,
    ...(seonSession ? { seonSession } : {}),
  });
  return data.data;
};

/** GET /widget/links/:linkId/operations/:operationId — allowed on consumed links. */
export const getOperation = async (
  linkId: string,
  operationId: string,
): Promise<IOperationResponse> => {
  const { data } = await widgetApi.get(
    `/widget/links/${linkId}/operations/${operationId}`,
  );
  return data.data;
};

export type CatalogEffectiveStatus = "AVAILABLE" | "UNAVAILABLE";

export interface IPayerCapability {
  payerCode: string;
  displayName: string;
  countryCode: string;
  payoutMethods: string[];
  supportedByCorridors: string[];
  effectiveStatus: CatalogEffectiveStatus;
}

export interface ICatalogPayersInput {
  originCountryCode?: string;
  destinationCountryCode: string;
  payoutMethod?: string;
  includeUnavailable?: boolean;
}

export const getCatalogPayers = async (
  input: ICatalogPayersInput,
): Promise<IPayerCapability[]> => {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(input)) {
    if (value === undefined || value === null || value === "") continue;
    params.set(key, String(value));
  }
  const { data } = await widgetApi.get(
    `/widget/catalog/payers?${params.toString()}`,
  );
  return data.data.items;
};

export interface IFundingMerchant {
  merchantId: string;
  name: string;
  logoUrl: string;
}

export const getFundingLocations = async (
  state?: string,
): Promise<IFundingMerchant[]> => {
  const { data } = await widgetApi.get("/widget/operations/funding-locations", {
    params: state ? { state } : undefined,
  });
  return data.data;
};

export interface IFundingStoreAddress {
  line1: string;
  city: string;
  state: string;
  zip: string;
  countryISO2: string;
  latitude: number;
  longitude: number;
}

/** Mirror of `FundingStoreResponseDto` — physical store, not the barcode chain. */
export interface IFundingStore {
  storeId: string;
  merchantId: string;
  name: string;
  logoUrl: string;
  address: IFundingStoreAddress;
  distanceMiles?: number;
}

export interface IFundingStoresQuery {
  lat?: number;
  lng?: number;
  merchantId?: string;
}

export const getFundingStores = async (
  query: IFundingStoresQuery = {},
): Promise<IFundingStore[]> => {
  const params: Record<string, string> = {};
  if (query.lat != null && query.lng != null) {
    params.lat = String(query.lat);
    params.lng = String(query.lng);
  }
  if (query.merchantId) {
    params.merchantId = query.merchantId;
  }
  const { data } = await widgetApi.get(
    "/widget/operations/funding-locations/stores",
    { params },
  );
  return data.data;
};

export interface ICashDepositLink {
  url: string;
  linkId: string;
}

export const createCashDepositLink = async (
  linkId: string,
  operationId: string,
  callbackUrl: string,
): Promise<ICashDepositLink> => {
  const { data } = await widgetApi.post(
    `/widget/links/${linkId}/operations/${operationId}/cash-deposit-link`,
    { callbackUrl },
  );
  return data.data;
};
